import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, 
  Download, 
  ZoomIn, 
  ZoomOut, 
  RotateCcw, 
  ChevronLeft, 
  ChevronRight,
  Maximize2,
  AlertTriangle,
  Lock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL - handles old preview URLs and relative paths
const getFullUrl = (url) => {
  if (!url) return null;
  // Already a relative path with /api prefix
  if (url.startsWith('/api/uploads')) return `${API}${url}`;
  // Handle /uploads/ paths which need /api prefix
  if (url.startsWith('/uploads')) return `${API}/api${url}`;
  // Old preview URL - extract path and use current API
  if (url.includes('.preview.emergentagent.com')) {
    const match = url.match(/\/api\/uploads\/.+$/);
    if (match) return `${API}${match[0]}`;
    const uploadMatch = url.match(/\/uploads\/.+$/);
    if (uploadMatch) return `${API}/api${uploadMatch[0]}`;
  }
  // External URL (http/https) - return as-is
  if (url.startsWith('http')) return url;
  // Relative path with leading slash
  if (url.startsWith('/')) return `${API}${url}`;
  // Relative path without leading slash
  return `${API}/${url}`;
};

export default function ReferenceSheetViewer() {
  const navigate = useNavigate();
  const { sheetId } = useParams();
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const [pdfAccessAllowed, setPdfAccessAllowed] = useState(false);
  const [pdfAccessReason, setPdfAccessReason] = useState(null);

  useEffect(() => {
    if (!sheetId) {
      setError('No sheet ID provided');
      setLoading(false);
      return;
    }
    fetchSheet();
    checkPdfAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetId]);

  const checkPdfAccess = async () => {
    try {
      const res = await fetch(`${API}/api/users/me/feature-access/reference_sheets_pdf`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPdfAccessAllowed(data.has_access);
        setPdfAccessReason(data.reason);
      }
    } catch (err) {
      console.error('Error checking PDF access:', err);
      setPdfAccessAllowed(false);
    }
  };

  const fetchSheet = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Try the learner endpoint (for published sheets)
      let res = await fetch(`${API}/api/reference-sheets/${sheetId}`, { credentials: 'include' });
      
      // If not found (404) or auth failed, try the admin endpoint (for draft preview)
      if (res.status === 404) {
        res = await fetch(`${API}/api/admin/reference-sheets/${sheetId}`, { credentials: 'include' });
      }
      
      if (!res.ok) {
        if (res.status === 404) {
          throw new Error('Reference sheet not found');
        }
        throw new Error('Failed to load reference sheet');
      }
      
      const data = await res.json();
      setSheet(data);
    } catch (err) {
      console.error('Error fetching sheet:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleResetZoom = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const handlePrevImage = () => {
    setCurrentImageIndex(prev => Math.max(0, prev - 1));
    handleResetZoom();
  };

  const handleNextImage = () => {
    if (sheet?.png_urls) {
      setCurrentImageIndex(prev => Math.min(sheet.png_urls.length - 1, prev + 1));
      handleResetZoom();
    }
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  const handleDownloadPdf = () => {
    if (sheet?.pdf_url) {
      window.open(getFullUrl(sheet.pdf_url), '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-500">Loading...</div>
      </div>
    );
  }

  if (error || !sheet) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F8FAFD] to-[#E8EEF3] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Error Loading Sheet</h3>
            <p className="text-slate-500 mb-6">{error}</p>
            <div className="flex gap-3 justify-center">
              <Button 
                onClick={fetchSheet} 
                variant="outline"
                data-testid="retry-btn"
              >
                Retry
              </Button>
              <Button 
                onClick={() => navigate('/reference-sheets')} 
                className="bg-[#FF9E00] hover:bg-[#E88D00] text-white"
              >
                Back to Reference Sheets
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentImage = getFullUrl(sheet.png_urls?.[currentImageIndex]);
  const totalImages = sheet.png_urls?.length || 0;

  return (
    <div className="min-h-screen bg-white text-slate-900 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/reference-sheets')}
                className="text-slate-600 hover:text-slate-900"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-lg font-semibold text-slate-900">{sheet.title}</h1>
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-xs">
                    {sheet.topic_key}
                  </Badge>
                  {sheet.subtopic_key && (
                    <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200 text-xs">
                      {sheet.subtopic_key}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-2">
              {sheet.pdf_url && pdfAccessAllowed && (
                <Button
                  onClick={handleDownloadPdf}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              )}
              {sheet.pdf_url && !pdfAccessAllowed && (
                <div className="flex items-center gap-2 text-slate-500">
                  <Lock className="w-4 h-4" />
                  <span className="text-sm">{pdfAccessReason || 'Upgrade to Pro for PDF downloads'}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Image Viewer */}
      <div className="flex-1 flex flex-col bg-slate-100">
        {/* Zoom Controls */}
        <div className="bg-white border-b border-slate-200 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleZoomOut}
              disabled={zoom <= 0.5}
              className="border-slate-200"
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-sm text-slate-600 w-16 text-center">{Math.round(zoom * 100)}%</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleZoomIn}
              disabled={zoom >= 3}
              className="border-slate-200"
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetZoom}
              className="border-slate-200 ml-2"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset
            </Button>
          </div>
          
          {/* Page Navigation */}
          {totalImages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevImage}
                disabled={currentImageIndex === 0}
                className="border-slate-200"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-600">
                {currentImageIndex + 1} of {totalImages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextImage}
                disabled={currentImageIndex === totalImages - 1}
                className="border-slate-200"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Image Container */}
        <div 
          ref={containerRef}
          className="flex-1 overflow-auto flex items-center justify-center p-4"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
          style={{ cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
        >
          {currentImage ? (
            <img
              src={currentImage}
              alt={`${sheet.title} - Page ${currentImageIndex + 1}`}
              className="max-w-full max-h-full shadow-lg rounded-lg bg-white"
              style={{
                transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                transformOrigin: 'center center'
              }}
              draggable={false}
              data-testid="sheet-image"
            />
          ) : (
            <div className="text-center text-slate-500">
              <Maximize2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p>No images available</p>
            </div>
          )}
        </div>

        {/* Thumbnail Strip */}
        {totalImages > 1 && (
          <div className="bg-white border-t border-slate-200 p-3">
            <div className="flex items-center justify-center gap-2 overflow-x-auto">
              {sheet.png_urls.map((url, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setCurrentImageIndex(idx);
                    handleResetZoom();
                  }}
                  className={`
                    flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all
                    ${idx === currentImageIndex 
                      ? 'border-blue-500 shadow-md' 
                      : 'border-slate-200 hover:border-slate-300'}
                  `}
                >
                  <img
                    src={getFullUrl(url)}
                    alt={`Thumbnail ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Description */}
      {sheet.description && (
        <div className="bg-white border-t border-slate-200 px-4 py-3">
          <p className="text-sm text-slate-600 max-w-3xl mx-auto">{sheet.description}</p>
        </div>
      )}
    </div>
  );
}
